/**
 * «Играть сейчас» — общий глагол близнецов (#2177, дефект приёмки 26.08).
 *
 * ЧТО БЫЛО. Кнопка ▶ на строке выборки звала только `selectSample`. Тот доводит состояние до
 * `paused`: проба выбрана, буфер загружен, звука нет. Владелец на проде нажал и не услышал
 * ничего — и был прав, потому что кнопка обещала звук, а делала выбор.
 *
 * ЧТО ТЕПЕРЬ. Один глагол делает обе половины: синхронизирует виджет библиотеки на эту пробу
 * (требование 4 владельца: дальше она управляется штатными органами виджета) и сразу играет.
 *
 * ПОЧЕМУ В ПАКЕТЕ, А НЕ В ПАНЕЛЯХ. Панелей шесть — три в кабинете, три в Studio, — и шесть
 * копий «выбрать, потом включить» разъехались бы молча: одна забыла бы вторую половину, и
 * дефект вернулся бы в одном из близнецов. Правило одно, дом у него один.
 *
 * ОТКАЗ НЕ ГЛОТАЕТСЯ. Если проба не загрузилась (чужое устройство, стёртый блоб), `selectSample`
 * оставляет статус `error` с причиной — играть нечего, и глагол молча не «успевает»: он
 * возвращает `false`, а вызывающий волен сказать об этом словом.
 */
import { getSamplePlaybackSnapshot, selectSample, togglePlayPause } from './service';
import type { SamplePlaybackTarget } from './types';

/**
 * Выбрать пробу в виджете и начать воспроизведение.
 *
 * @returns `true` — звук пошёл; `false` — проба не загрузилась, играть нечего.
 */
export async function playSampleNow(
  target: SamplePlaybackTarget,
  deps: {
    readonly select: typeof selectSample;
    readonly toggle: typeof togglePlayPause;
    /**
     * Живой статус плеера. По умолчанию берётся у СЕРВИСА, а не у React-пропа: проп несёт
     * состояние ПРОШЛОГО рендера, и сразу после `select` он ещё старый — на `playing` глагол
     * счёл бы ошибку успехом, на `error` наоборот. Ревью #2184 поймало это раньше прода.
     */
    readonly statusOf?: () => string;
  },
): Promise<boolean> {
  await deps.select(target);
  // Состояние спрашивается ПОСЛЕ выбора: `selectSample` ставит `error`, если буфер не пришёл.
  // Жать «играть» поверх ошибки значило бы прятать причину за молчаливым бездействием.
  const statusOf = deps.statusOf ?? (() => getSamplePlaybackSnapshot().status);
  if (statusOf() === 'error') return false;
  await deps.toggle();
  return true;
}
